import { useEffect } from '@wordpress/element';
import GenerateCss from '../functions/GenerateCss.jsx';
import useSyncCompiledCss from '../hooks/useSyncCompiledCss.jsx';

function Style( { attributes, setAttributes } ) {
	const { blockId } = attributes;
	const unique = blockId
		? `.alpha-image-blocks.alpha-image-block-${ blockId }`
		: '.wp-block-alphablock-image';
	const {
		containerBorder,
		containerBorderRadius = {},
		margin,
		padding,
		alignment,
		imageHeight,
		imageWidth,
		horizontalAlignment,
		imageOpacity,
		imageFilterGrayscale,
		imageFilterBrightness,
		imageFilterContrast,
		imageFilterSaturate,
		imageFilterSepia,
		imageFilterBlur,
		imageBoxShadow,
		overlayTypography,
		overlayTextColor,
		imageMaskShape,
		containerHeight,
		containerWidth,
	} = attributes;

	const filterString = [
		`grayscale(${ imageFilterGrayscale?.desktop?.val ?? 0 }%)`,
		`blur(${ imageFilterBlur?.desktop?.val ?? 0 }px)`,
		`sepia(${ imageFilterSepia?.desktop?.val ?? 0 }%)`,
		`saturate(${ imageFilterSaturate?.desktop?.val ?? 100 }%)`,
		`contrast(${ imageFilterContrast?.desktop?.val ?? 100 }%)`,
		`brightness(${ imageFilterBrightness?.desktop?.val ?? 100 }%)`,
	].join( ' ' );

	const opacityValue = ( imageOpacity?.desktop?.val ?? 100 ) / 100;

	const getMaskCss = ( shape ) => {
		const masks = {
			circle: 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxMDAiIGN5PSIxMDAiIHI9IjEwMCIvPjwvc3ZnPg==) no-repeat center',
			diamond:
				'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cG9seWdvbiBwb2ludHM9IjEwMCwwIDIwMCwxMDAgMTAwLDIwMCAwLDEwMCIvPjwvc3ZnPg==) no-repeat center',
			hazagon:
				'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cG9seWdvbiBwb2ludHM9IjUwLDAgMTUwLDAgMjAwLDg2LjYgMTUwLDE3My4yIDUwLDE3My4yIDAsODYuNiIvPjwvc3ZnPg==) no-repeat center',
			rounded:
				'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgcng9IjMwIiByeT0iMzAiLz48L3N2Zz4=) no-repeat center',
			'shape-1':
				'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMCAwIEwyMDAgMCBMMjAwIDIwMCBMMCAyMDBaIE0xMDAgMCBMMjAwIDEwMCBMMTAwIDIwMCBMMCAxMDBaIi8+PC9zdmc+) no-repeat center',
			'shape-2':
				'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMCAwIEwyMDAgMCBMMjAwIDIwMCBMMCAyMDBaIE0xMDAgMCBMMjAwIDEwMCBMMTAwIDIwMCBMMCAxMDBaIiB0cmFuc2Zvcm09InJvdGF0ZSg0NSAxMDAgMTAwKSIvPjwvc3ZnPg==) no-repeat center',
			'shape-3':
				'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMCAwIEwyMDAgMCBMMjAwIDIwMCBMMCAyMDBaIE0xMDAgMCBMMjAwIDEwMCBMMTAwIDIwMCBMMCAxMDBaIiB0cmFuc2Zvcm09InNrZXdYKD MwKSIvPjwvc3ZnPg==) no-repeat center',
			'shape-4':
				'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMCAwIEwyMDAgMCBMMjAwIDIwMCBMMCAyMDBaIE0xMDAgMCBMMjAwIDEwMCBMMTAwIDIwMCBMMCAxMDBaIiB0cmFuc2Zvcm09InNrZXdZKD MwKSIvPjwvc3ZnPg==) no-repeat center',
		};

		if ( masks[ shape ] ) {
			return [
				GenerateCss.css( '-webkit-mask', masks[ shape ] ),
				GenerateCss.css( 'mask', masks[ shape ] ),
				GenerateCss.css( '-webkit-mask-size', '100%' ),
				GenerateCss.css( 'mask-size', '100%' ),
			];
		}
		return [];
	};

	const finalImageWidth = containerWidth?.desktop?.val
		? {
				desktop: { val: 100, unit: '%' },
				tablet: { val: 100, unit: '%' },
				mobile: { val: 100, unit: '%' },
		  }
		: imageWidth;

	const finalImageHeight = containerHeight?.desktop?.val
		? {
				desktop: { val: 100, unit: '%' },
				tablet: { val: 100, unit: '%' },
				mobile: { val: 100, unit: '%' },
		  }
		: imageHeight;

	const CssObj = {
		[ unique ]: [
			GenerateCss.border( containerBorder ),
			GenerateCss.borderRadius( containerBorderRadius ),
			GenerateCss.margin( margin ),
			GenerateCss.padding( padding ),
			GenerateCss.css( 'justify-content', alignment ),
			GenerateCss.css( 'text-align', horizontalAlignment ),
			GenerateCss.css( 'overflow', 'hidden' ),
			GenerateCss.lengthProp( 'height', containerHeight ),
			GenerateCss.lengthProp( 'width', containerWidth ),
		],
		[ `${unique} > div ` ]: [
			GenerateCss.lengthProp( 'height', containerHeight ),
			GenerateCss.lengthProp( 'width', containerWidth ),
		],
		[ `${ unique } img` ]: [
			GenerateCss.lengthProp( 'height', finalImageHeight ),
			GenerateCss.lengthProp( 'width', finalImageWidth ),
			GenerateCss.css( 'opacity', opacityValue ),
			GenerateCss.css( 'filter', filterString ),
			GenerateCss.boxShadow( imageBoxShadow ),
			...getMaskCss( imageMaskShape ),
		],
		[ `${ unique } .overlay-text` ]: [
			GenerateCss.typography( overlayTypography ),
			// GenerateCss.lengthProp("height", imageHeight),
			GenerateCss.lengthProp( 'width', imageWidth ),
			GenerateCss.textColor( overlayTextColor ),
		],
	};

	const compiledCss = GenerateCss.CompiledCss( CssObj );

	useSyncCompiledCss( compiledCss, setAttributes );

	return <style>{ compiledCss }</style>;
}

export default Style;
